import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Typography, IconButton, Chip, Tooltip, Collapse,
    Divider, useTheme, Dialog, DialogTitle, DialogContent,
    DialogActions, Button, TextField, CircularProgress, Autocomplete,
    Snackbar, Alert as MuiAlert, Tabs, Tab,
} from '@mui/material';
import {
    Close as CloseIcon, ContentCopy as CopyIcon,
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    Shield as ShieldIcon, Code as CodeIcon,
    CheckCircle as CheckCircleIcon, Person as PersonIcon,
    Computer as ComputerIcon, OpenInNew as OpenInNewIcon,
    Gavel as ActionIcon,
    ConfirmationNumber as TicketIcon,
    Link as LinkIcon,
    Layers as LayersIcon,
    Schedule as ScheduleIcon,
    Terminal as TerminalIcon,
} from '@mui/icons-material';
import { Alert, RootState, Ticket, TicketPriority } from '../types';
import NewTicketForm from './NewTicketForm';
import PlaybookTab from './playbook/PlaybookTab';
import { clearSelectedAlert } from '../store/slices/alertsSlice';
import { addAlertsToTicket, fetchTickets } from '../store/slices/ticketsSlice';
import { getSeverityChipSx, getStatusColor, getPriorityColor } from '../theme/mappers/ticketMappers';
import { colorUtils } from '../theme';
import { getRelativeTimeString } from '../utils/dateUtils';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';

// ============================================================================
// SUB-COMPONENTS (matching TicketDetails patterns)
// ============================================================================

const CollapsibleSection: React.FC<{
    title: string; icon: React.ReactNode; badge?: string | number;
    children: React.ReactNode; defaultExpanded?: boolean;
}> = ({ title, icon, badge, children, defaultExpanded = true }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const theme = useTheme();

    return (
        <Box sx={{ mb: 2 }}>
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, px: 1,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                    '&:hover .section-icon': { color: theme.palette.ui.iconActive },
                }}
            >
                <Box
                    className="section-icon"
                    sx={{
                        color: expanded ? theme.palette.ui.iconActive : theme.palette.ui.iconDefault,
                        display: 'flex',
                        transition: 'color 0.2s',
                    }}
                >
                    {icon}
                </Box>
                <Typography variant="subtitle2" sx={{ flex: 1, color: theme.palette.text.secondary, fontWeight: 600 }}>
                    {title}
                </Typography>
                {badge !== undefined && (
                    <Typography variant="caption" sx={{
                        color: theme.palette.text.tertiary,
                        fontWeight: 600,
                        mr: 1,
                        fontFamily: '"JetBrains Mono", monospace',
                    }}>
                        {badge}
                    </Typography>
                )}
                <Box sx={{ color: theme.palette.text.tertiary }}>
                    {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Box>
            </Box>
            <Collapse in={expanded}>
                <Box sx={{ pt: 1.5, pl: 4, pr: 1 }}>{children}</Box>
            </Collapse>
        </Box>
    );
};

const EntityRow: React.FC<{
    label: string; icon: React.ReactNode;
    primaryValue: string;
    details: { label: string; value: string }[];
    statusBadge?: { label: string; color: string };
}> = ({ label, icon, primaryValue, details, statusBadge }) => {
    const theme = useTheme();
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 1,
            py: 1,
            px: 1.5,
            borderRadius: 1.5,
            backgroundColor: theme.palette.background.tertiary,
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: theme.palette.interactive.hover },
        }}>
            <Box sx={{ color: theme.palette.ui.iconDefault, display: 'flex' }}>{icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{
                    color: theme.palette.text.tertiary,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    display: 'block',
                    mb: 0.25,
                }}>
                    {label}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{
                        fontFamily: '"JetBrains Mono", monospace',
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                    }}>
                        {primaryValue}
                    </Typography>
                    {details.map((d, i) => (
                        <Typography key={i} variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                        }}>
                            {d.label}: <Box component="span" sx={{ color: theme.palette.text.secondary }}>{d.value}</Box>
                        </Typography>
                    ))}
                </Box>
            </Box>
            {statusBadge && (
                <Chip
                    label={statusBadge.label}
                    size="small"
                    sx={{
                        height: 18,
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        backgroundColor: colorUtils.withOpacity(statusBadge.color, 0.08),
                        color: statusBadge.color,
                        border: `1px solid ${colorUtils.withOpacity(statusBadge.color, 0.19)}`,
                    }}
                />
            )}
        </Box>
    );
};

const CopyableValue: React.FC<{ value: string; label?: string }> = ({ value, label }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            '&:hover .copy-btn': { opacity: 1 }
        }}>
            <Typography variant="caption" sx={{
                fontFamily: '"JetBrains Mono", monospace',
                color: theme.palette.text.secondary,
            }}>
                {label || value}
            </Typography>
            <IconButton
                className="copy-btn"
                size="small"
                onClick={handleCopy}
                sx={{ opacity: 0, transition: 'opacity 0.2s', p: 0.25 }}
            >
                {copied
                    ? <CheckCircleIcon sx={{ fontSize: 12, color: theme.palette.severity.low }} />
                    : <CopyIcon sx={{ fontSize: 12, color: theme.palette.ui.iconDefault }} />
                }
            </IconButton>
        </Box>
    );
};

const MetricBox: React.FC<{
    label: string;
    value: string | number;
    color?: string;
    subLabel?: string;
}> = ({ label, value, color, subLabel }) => {
    const theme = useTheme();
    return (
        <Box sx={{ minWidth: 80 }}>
            <Typography variant="caption" sx={{
                color: theme.palette.text.tertiary,
                fontSize: '0.65rem',
                letterSpacing: '0.08em',
                display: 'block',
                mb: 0.5,
            }}>
                {label}
            </Typography>
            <Typography sx={{
                color: color || theme.palette.text.primary,
                fontWeight: 600,
                fontSize: '0.9rem',
            }}>
                {value}
            </Typography>
            {subLabel && (
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontSize: '0.65rem' }}>
                    {subLabel}
                </Typography>
            )}
        </Box>
    );
};

const CopyIconButton: React.FC<{ value: string }> = ({ value }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy'} arrow>
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25, color: theme.palette.ui.iconDefault }}>
                {copied
                    ? <CheckCircleIcon sx={{ fontSize: 14, color: theme.palette.severity.low }} />
                    : <CopyIcon sx={{ fontSize: 14 }} />
                }
            </IconButton>
        </Tooltip>
    );
};

const TimelineRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.25 }}>
            <Typography variant="caption" sx={{
                color: theme.palette.text.tertiary,
                fontSize: '0.6rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                minWidth: 64,
                pt: 0.25,
            }}>
                {label.toUpperCase()}
            </Typography>
            <Box sx={{
                width: 6, height: 6, borderRadius: '50%',
                backgroundColor: theme.palette.border.strong,
                flexShrink: 0,
                mt: 0.5,
            }} />
            <Box>
                <Typography variant="caption" sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    color: theme.palette.text.secondary,
                    fontSize: '0.72rem',
                    display: 'block',
                }}>
                    {new Date(value).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{
                    color: theme.palette.text.tertiary,
                    fontSize: '0.65rem',
                }}>
                    {getRelativeTimeString(value)}
                </Typography>
            </Box>
        </Box>
    );
};

const TicketIdChip: React.FC<{ ticketId: string }> = ({ ticketId }) => {
    const theme = useTheme();
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(ticketId);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy ticket ID'} arrow>
            <Chip
                label={ticketId}
                size="small"
                onClick={handleCopy}
                icon={copied
                    ? <CheckCircleIcon sx={{ fontSize: 12, color: `${theme.palette.severity.low} !important` }} />
                    : <CopyIcon sx={{ fontSize: 12, color: `${theme.palette.text.tertiary} !important` }} />
                }
                sx={{
                    height: 22,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    fontFamily: '"JetBrains Mono", monospace',
                    cursor: 'pointer',
                    backgroundColor: 'transparent',
                    color: theme.palette.text.secondary,
                    border: `1px solid ${theme.palette.border.main}`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                        backgroundColor: colorUtils.withOpacity(theme.palette.text.primary, 0.04),
                        borderColor: theme.palette.border.strong,
                        color: theme.palette.text.primary,
                    },
                }}
            />
        </Tooltip>
    );
};

// ============================================================================
// HELPERS
// ============================================================================

/** Searches raw_data at multiple nesting levels for the first matching key. */
const extractRawField = (raw: any, ...keys: string[]): string | null => {
    if (!raw) return null;
    const sources = [
        raw,
        raw.event,
        raw.metadata,
        raw.data,
        raw.alert,
        Array.isArray(raw.behaviors) ? raw.behaviors[0] : undefined,
    ].filter(Boolean);
    for (const source of sources) {
        if (!source || typeof source !== 'object') continue;
        for (const key of keys) {
            const val = source[key];
            if (val !== undefined && val !== null && val !== '') return String(val);
        }
    }
    return null;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface AlertDetailsProps {
    alert: Alert;
}

const AlertDetails: React.FC<AlertDetailsProps> = ({ alert }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [ticketCreatedSuccess, setTicketCreatedSuccess] = useState(false);
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [idCopied, setIdCopied] = useState(false);
    const [linkSelectedTicket, setLinkSelectedTicket] = useState<Ticket | null>(null);
    const [linkError, setLinkError] = useState<string | null>(null);
    const [linkSuccess, setLinkSuccess] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const { isLoading: isLinking, tickets: allTickets } = useSelector((state: RootState) => state.tickets);

    // Fetch tickets when the link dialog opens
    useEffect(() => {
        if (linkDialogOpen && allTickets.length === 0) {
            dispatch(fetchTickets({}) as any);
        }
    }, [linkDialogOpen]);

    // Filter to tickets for the same client, excluding already-linked ones
    const clientTickets = useMemo(() =>
        allTickets.filter(t => t.client_id === alert.client_id && t.id !== alert.ticket_id),
        [allTickets, alert.client_id, alert.ticket_id]
    );

    const handleLinkDialogClose = () => {
        setLinkDialogOpen(false);
        setLinkSelectedTicket(null);
        setLinkError(null);
        setLinkSuccess(false);
    };

    const handleLinkToTicket = () => {
        if (!linkSelectedTicket) {
            setLinkError('Please select a ticket');
            return;
        }
        setLinkError(null);
        setLinkSuccess(false);
        dispatch(addAlertsToTicket({ ticketId: linkSelectedTicket.id, alertIds: [alert.id] }) as any)
            .unwrap()
            .then(() => {
                setLinkSuccess(true);
                setTimeout(() => {
                    handleLinkDialogClose();
                }, 1200);
            })
            .catch((err: any) => {
                const msg = typeof err === 'string' ? err : (err?.message || 'Failed to link alert to ticket');
                setLinkError(msg);
            });
    };

    const mapSeverityToPriority = (severity: string): TicketPriority => {
        switch (severity?.toLowerCase()) {
            case 'critical': return TicketPriority.CRITICAL;
            case 'high': return TicketPriority.HIGH;
            case 'medium': return TicketPriority.MEDIUM;
            case 'low': return TicketPriority.LOW;
            default: return TicketPriority.MEDIUM;
        }
    };

    const getSeverityColor = (severity: string) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return theme.palette.severity.critical;
            case 'high': return theme.palette.severity.high;
            case 'medium': return theme.palette.severity.medium;
            case 'low': return theme.palette.severity.low;
            default: return theme.palette.severity.low;
        }
    };

    const normalizeSeverity = (severity: string) => {
        const s = severity?.toLowerCase() || 'low';
        if (['critical', 'high', 'medium', 'low'].includes(s)) {
            return s.charAt(0).toUpperCase() + s.slice(1);
        }
        return 'Low';
    };

    const getRiskScoreColor = (riskScore: number) => {
        if (riskScore >= 80) return theme.palette.severity.critical;
        if (riskScore >= 60) return theme.palette.severity.high;
        if (riskScore >= 40) return theme.palette.severity.medium;
        if (riskScore >= 20) return theme.palette.severity.low;
        return theme.palette.text.secondary;
    };

    const getRiskScoreLabel = (riskScore: number) => {
        if (riskScore >= 80) return 'CRITICAL';
        if (riskScore >= 60) return 'HIGH';
        if (riskScore >= 40) return 'MEDIUM';
        if (riskScore >= 20) return 'LOW';
        return 'INFO';
    };

    const severityColor = getSeverityColor(alert.severity);
    const severityLabel = normalizeSeverity(alert.severity);
    const riskColor = getRiskScoreColor(alert.risk_score);
    const riskLabel = getRiskScoreLabel(alert.risk_score);

    // Detection signal fields extracted from raw_data
    const commandLine = extractRawField(alert.raw_data, 'CommandLine', 'command_line', 'cmdline', 'ProcessCommandLine');
    const detectionName = extractRawField(alert.raw_data, 'Name', 'detection_name', 'alert_name', 'DetectName');
    const sourceLink = extractRawField(alert.raw_data, 'FalconHostLink', 'falcon_host_link', 'url', 'link', 'source_url');
    const rawVerdict = extractRawField(alert.raw_data, 'PatternDispositionDescription', 'disposition', 'action', 'verdict');

    const verdictInfo = rawVerdict ? (() => {
        const v = rawVerdict.toLowerCase();
        if (v.includes('block') || v.includes('prevent') || v.includes('kill'))
            return { label: 'BLOCKED', color: theme.palette.severity.critical };
        if (v.includes('detect') || v.includes('alert') || v.includes('observed'))
            return { label: 'DETECTED', color: theme.palette.severity.medium };
        if (v.includes('allow') || v.includes('no action') || v.includes('monitor'))
            return { label: 'ALLOWED', color: theme.palette.severity.low };
        return { label: rawVerdict.toUpperCase().slice(0, 20), color: theme.palette.text.secondary };
    })() : null;

    const hasDetectionSignal = !!(
        verdictInfo ||
        commandLine ||
        (detectionName && detectionName !== alert.title) ||
        sourceLink
    );

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: theme.palette.background.default }}>

            {/* ================================================================
                HEADER: Alert Summary
            ================================================================ */}
            <Box sx={{ p: 3, borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                {/* Top row: Severity, timestamp, close */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    {/* Metadata side */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                            label={severityLabel}
                            size="small"
                            sx={getSeverityChipSx(severityColor)}
                        />
                        <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                            {alert.detected_at
                                ? new Date(alert.detected_at).toLocaleString()
                                : new Date(alert.created_at).toLocaleString()
                            }
                        </Typography>
                    </Box>

                    {/* Actions side */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: theme.palette.background.tertiary,
                        borderRadius: 1.5,
                        p: 0.5,
                        gap: 0.25,
                    }}>
                        <Tooltip title="Link to Existing Ticket">
                            <IconButton size="small" sx={{ p: 0.75 }} onClick={() => setLinkDialogOpen(true)}>
                                <LinkIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Create Ticket">
                            <IconButton size="small" sx={{ p: 0.75 }} onClick={() => setCreateDialogOpen(true)}>
                                <TicketIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" sx={{ p: 0.75 }} onClick={() => dispatch(clearSelectedAlert())}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </Box>

                {/* Title + ID */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.4, flex: 1, minWidth: 0 }}>
                        {alert.title}
                    </Typography>
                    <Tooltip title={idCopied ? 'Copied!' : 'Copy alert ID'} placement="top" arrow>
                        <Chip
                            label={`#${alert.id.slice(0, 8)}…`}
                            size="small"
                            onClick={() => {
                                navigator.clipboard.writeText(alert.id);
                                setIdCopied(true);
                                setTimeout(() => setIdCopied(false), 1500);
                            }}
                            sx={{
                                cursor: 'pointer',
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                height: 22,
                                color: idCopied ? theme.palette.success.main : theme.palette.text.tertiary,
                                backgroundColor: theme.palette.background.tertiary,
                                border: `1px solid ${theme.palette.border.light}`,
                                transition: 'color 0.2s',
                                flexShrink: 0,
                                '& .MuiChip-label': { px: 1 },
                                '&:hover': { color: theme.palette.text.primary, borderColor: theme.palette.border.main },
                            }}
                        />
                    </Tooltip>
                </Box>

                {/* Metrics row */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 3,
                    flexWrap: 'nowrap',
                    mt: 1,
                    overflow: 'hidden',
                }}>
                    {/* Risk Score */}
                    <Box sx={{ minWidth: 'fit-content' }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            letterSpacing: '0.08em',
                            display: 'block',
                            mb: 0.5,
                            whiteSpace: 'nowrap',
                        }}>
                            RISK SCORE
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                            <Typography sx={{
                                color: riskColor,
                                fontWeight: 700,
                                fontSize: '1.5rem',
                                lineHeight: 1,
                                fontFamily: '"JetBrains Mono", monospace',
                            }}>
                                {alert.risk_score}
                            </Typography>
                            <Typography variant="caption" sx={{
                                color: riskColor,
                                fontWeight: 600,
                                fontSize: '0.6rem',
                                textTransform: 'uppercase',
                            }}>
                                {riskLabel}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Action Taken */}
                    {alert.action_taken && (
                        <MetricBox
                            label="ACTION TAKEN"
                            value={alert.action_taken}
                        />
                    )}

                    {/* Source */}
                    {alert.integration_type && (
                        <MetricBox
                            label="SOURCE"
                            value={alert.integration_type}
                        />
                    )}

                    {/* Detected */}
                    {alert.detected_at && (
                        <MetricBox
                            label="DETECTED"
                            value={getRelativeTimeString(alert.detected_at)}
                        />
                    )}

                    <Box sx={{ flex: 1, minWidth: 16 }} />
                </Box>

                {/* Linked Ticket ID */}
                {alert.ticket_id && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                        }}>
                            TICKET
                        </Typography>
                        <TicketIdChip ticketId={alert.ticket_id} />
                    </Box>
                )}
            </Box>

            {/* ================================================================
                TABS
            ================================================================ */}
            <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }}>
                <Tabs
                    value={activeTab}
                    onChange={(_, v) => setActiveTab(v)}
                    sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: '0.75rem', fontWeight: 600 } }}
                >
                    <Tab label="Details" />
                    <Tab label="Playbook" />
                </Tabs>
            </Box>

            {/* ================================================================
                BODY: Scrollable content
            ================================================================ */}
            {activeTab === 1 && (
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    <PlaybookTab alertId={alert.id} />
                </Box>
            )}
            <Box sx={{
                display: activeTab === 0 ? 'block' : 'none',
                flex: 1,
                overflowY: 'auto',
                overflowX: 'hidden',
                boxSizing: 'border-box',
                p: 3,
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.border.main,
                    borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    backgroundColor: theme.palette.border.strong,
                },
            }}>

                {/* Detection Signal */}
                {hasDetectionSignal && (
                    <CollapsibleSection
                        title="DETECTION SIGNAL"
                        icon={<ActionIcon fontSize="small" />}
                    >
                        {/* Verdict + Detection Name */}
                        {(verdictInfo || (detectionName && detectionName !== alert.title)) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                                {verdictInfo && (
                                    <Chip
                                        label={verdictInfo.label}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.65rem',
                                            fontWeight: 700,
                                            backgroundColor: colorUtils.withOpacity(verdictInfo.color, 0.12),
                                            color: verdictInfo.color,
                                            border: `1px solid ${colorUtils.withOpacity(verdictInfo.color, 0.25)}`,
                                        }}
                                    />
                                )}
                                {detectionName && detectionName !== alert.title && (
                                    <Typography variant="caption" sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.75rem',
                                    }}>
                                        {detectionName}
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {/* CommandLine block */}
                        {commandLine && (
                            <Box sx={{ mb: 1.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <TerminalIcon sx={{ fontSize: 12, color: theme.palette.text.tertiary }} />
                                        <Typography variant="caption" sx={{
                                            color: theme.palette.text.tertiary,
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            letterSpacing: '0.05em',
                                        }}>
                                            COMMAND LINE
                                        </Typography>
                                    </Box>
                                    <CopyIconButton value={commandLine} />
                                </Box>
                                <Box sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: theme.palette.background.inset,
                                    border: `1px solid ${theme.palette.border.light}`,
                                    overflowX: 'auto',
                                }}>
                                    <Typography sx={{
                                        fontFamily: '"JetBrains Mono", monospace',
                                        fontSize: '0.72rem',
                                        color: theme.palette.text.secondary,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        lineHeight: 1.5,
                                    }}>
                                        {commandLine}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Source link */}
                        {sourceLink && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    size="small"
                                    endIcon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                                    href={sourceLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    component="a"
                                    sx={{
                                        fontSize: '0.7rem',
                                        color: theme.palette.text.tertiary,
                                        textTransform: 'none',
                                        '&:hover': { color: theme.palette.text.secondary },
                                    }}
                                >
                                    View in source platform
                                </Button>
                            </Box>
                        )}
                    </CollapsibleSection>
                )}

                {/* Description */}
                {alert.description && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="caption" sx={{
                            color: theme.palette.text.tertiary,
                            fontSize: '0.65rem',
                            letterSpacing: '0.08em',
                            display: 'block',
                            mb: 1,
                        }}>
                            DESCRIPTION
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.primary, lineHeight: 1.6 }}>
                            {alert.description}
                        </Typography>
                    </Box>
                )}

                {/* Affected Entities */}
                {(alert.username || alert.hostname || alert.ip_address || alert.file_name || alert.file_hash) && (
                    <CollapsibleSection
                        title="AFFECTED ENTITIES"
                        icon={<LayersIcon fontSize="small" />}
                        badge={[
                            alert.username || alert.hostname,
                            alert.ip_address || alert.file_name || alert.file_hash,
                        ].filter(Boolean).length}
                    >
                        {(alert.username || alert.hostname) && (
                            <EntityRow
                                label="IDENTITY"
                                icon={<PersonIcon sx={{ fontSize: 18 }} />}
                                primaryValue={alert.username || alert.hostname || ''}
                                details={[
                                    ...(alert.username && alert.hostname ? [{ label: 'Host', value: alert.hostname }] : []),
                                ]}
                            />
                        )}
                        {(alert.ip_address || alert.file_name || alert.file_hash) && (
                            <EntityRow
                                label="ASSET"
                                icon={<ComputerIcon sx={{ fontSize: 18 }} />}
                                primaryValue={alert.ip_address || alert.file_name || ''}
                                details={[
                                    ...(alert.ip_address && alert.file_name ? [{ label: 'File', value: alert.file_name }] : []),
                                    ...(alert.file_hash ? [{ label: 'Hash', value: `${alert.file_hash.slice(0, 12)}…` }] : []),
                                ]}
                            />
                        )}
                    </CollapsibleSection>
                )}

                {/* Timeline */}
                {(alert.occurred_at || alert.detected_at) && (
                    <CollapsibleSection
                        title="TIMELINE"
                        icon={<ScheduleIcon fontSize="small" />}
                    >
                        <Box sx={{ pl: 0.5 }}>
                            {alert.occurred_at && (
                                <TimelineRow label="Occurred" value={alert.occurred_at} />
                            )}
                            {alert.detected_at && (
                                <TimelineRow label="Detected" value={alert.detected_at} />
                            )}
                            <TimelineRow label="Ingested" value={alert.created_at} />
                        </Box>
                    </CollapsibleSection>
                )}

                {/* MITRE ATT&CK Mapping */}
                {(alert.mitre_tactic || alert.mitre_technique) && (
                    <CollapsibleSection
                        title="MITRE ATT&CK MAPPING"
                        icon={<ShieldIcon fontSize="small" />}
                    >
                        <Box sx={{
                            display: 'flex', alignItems: 'center', gap: 2,
                            p: 1.5, borderRadius: 1,
                            backgroundColor: theme.palette.background.tertiary,
                        }}>
                            <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    {alert.mitre_tactic && (
                                        <Typography variant="caption" sx={{
                                            color: theme.palette.text.tertiary,
                                            fontSize: '0.65rem',
                                        }}>
                                            {alert.mitre_tactic}
                                        </Typography>
                                    )}
                                </Box>
                                {alert.mitre_technique && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                                        {alert.mitre_technique}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </CollapsibleSection>
                )}

                {/* Source */}
                {alert.integration_type && (
                    <CollapsibleSection
                        title="SOURCE"
                        icon={<OpenInNewIcon fontSize="small" />}
                        defaultExpanded={true}
                    >
                        <Box sx={{
                            p: 2, borderRadius: 1,
                            backgroundColor: theme.palette.background.tertiary,
                        }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
                                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                                    Integration
                                </Typography>
                                <Typography variant="caption" sx={{
                                    color: theme.palette.text.primary,
                                    fontWeight: 500,
                                    textTransform: 'capitalize',
                                }}>
                                    {alert.integration_type}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
                                    Alert ID
                                </Typography>
                                <CopyableValue value={alert.id} />
                            </Box>
                        </Box>
                    </CollapsibleSection>
                )}

                {/* Raw Data */}
                {alert.raw_data && (
                    <CollapsibleSection
                        title="RAW DATA"
                        icon={<CodeIcon fontSize="small" />}
                        defaultExpanded={true}
                    >
                        <Box sx={{
                            borderRadius: 1,
                            overflow: 'hidden',
                            backgroundColor: theme.palette.background.inset,
                            '& pre': { margin: 0, fontSize: '0.75rem !important' },
                            '& pre::-webkit-scrollbar': {
                                width: '5px',
                                height: '5px',
                            },
                            '& pre::-webkit-scrollbar-track': {
                                backgroundColor: 'transparent',
                            },
                            '& pre::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.border.main,
                                borderRadius: '3px',
                            },
                            '& pre::-webkit-scrollbar-thumb:hover': {
                                backgroundColor: theme.palette.border.strong,
                            },
                            '& pre::-webkit-scrollbar-corner': {
                                backgroundColor: 'transparent',
                            },
                        }}>
                            <SyntaxHighlighter
                                language="json"
                                style={theme.palette.mode === 'light' ? vs : vscDarkPlus}
                                customStyle={{
                                    backgroundColor: 'transparent',
                                    padding: '1rem',
                                    maxHeight: '400px',
                                    overflow: 'auto',
                                }}
                            >
                                {JSON.stringify(alert.raw_data, null, 2)}
                            </SyntaxHighlighter>
                        </Box>
                    </CollapsibleSection>
                )}
            </Box>

            {/* Link to Ticket Dialog */}
            <Dialog
                open={linkDialogOpen}
                onClose={handleLinkDialogClose}
                maxWidth="sm"
                fullWidth
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 2,
                        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
                    },
                }}
            >
                <DialogTitle sx={{
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.border.light}`,
                    fontWeight: 700,
                }}>
                    Link Alert to Ticket
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2.5, mt: 1 }}>
                        Select a ticket for <strong>{alert.client_name}</strong> to link this alert to.
                    </Typography>
                    <Autocomplete
                        options={clientTickets}
                        value={linkSelectedTicket}
                        onChange={(_e, val) => { setLinkSelectedTicket(val); setLinkError(null); }}
                        disabled={isLinking || linkSuccess}
                        getOptionLabel={(t) => t.title}
                        noOptionsText={
                            allTickets.length === 0
                                ? 'Loading tickets...'
                                : `No open tickets found for ${alert.client_name}`
                        }
                        renderOption={(props, t) => (
                            <Box
                                component="li"
                                {...props}
                                key={t.id}
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: '10px !important', gap: 0.5 }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.3 }}>
                                    {t.title}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Chip
                                        label={t.status.replace('_', ' ')}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.62rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            backgroundColor: colorUtils.withOpacity(getStatusColor(t.status, theme), 0.1),
                                            color: getStatusColor(t.status, theme),
                                            border: `1px solid ${colorUtils.withOpacity(getStatusColor(t.status, theme), 0.22)}`,
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                    <Chip
                                        label={t.priority}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.62rem',
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            backgroundColor: colorUtils.withOpacity(getPriorityColor(t.priority, theme), 0.12),
                                            color: getPriorityColor(t.priority, theme),
                                            border: `1px solid ${colorUtils.withOpacity(getPriorityColor(t.priority, theme), 0.25)}`,
                                            '& .MuiChip-label': { px: 0.75 },
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontFamily: '"JetBrains Mono", monospace', fontSize: '0.65rem' }}>
                                        {t.id.slice(0, 8)}…
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search tickets..."
                                error={!!linkError}
                                helperText={linkError}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: theme.palette.background.tertiary,
                                        '& fieldset': { borderColor: linkError ? theme.palette.error.main : theme.palette.border.main },
                                        '&:hover fieldset': { borderColor: theme.palette.text.secondary },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.text.primary },
                                    },
                                }}
                            />
                        )}
                    />
                    {linkSelectedTicket && (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, backgroundColor: theme.palette.background.tertiary, border: `1px solid ${theme.palette.border.light}` }}>
                            <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mb: 0.5 }}>
                                Selected Ticket
                            </Typography>
                            <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                                {linkSelectedTicket.title}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, fontFamily: '"JetBrains Mono", monospace' }}>
                                {linkSelectedTicket.id}
                            </Typography>
                        </Box>
                    )}
                    {linkSuccess && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                            <CheckCircleIcon sx={{ fontSize: 16, color: theme.palette.severity.low }} />
                            <Typography variant="caption" sx={{ color: theme.palette.severity.low, fontWeight: 600 }}>
                                Alert linked successfully
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{
                    borderTop: `1px solid ${theme.palette.border.light}`,
                    p: 2,
                }}>
                    <Button
                        onClick={handleLinkDialogClose}
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': { backgroundColor: theme.palette.interactive.hover },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleLinkToTicket}
                        disabled={isLinking || linkSuccess || !linkSelectedTicket}
                    >
                        {isLinking ? <CircularProgress size={20} /> : 'Link'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create Ticket Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                maxWidth="md"
                fullWidth
                disableScrollLock
                PaperProps={{
                    sx: {
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.border.main}`,
                        borderRadius: 2,
                        boxShadow: '0 24px 48px rgba(0, 0, 0, 0.4)',
                    },
                }}
            >
                <DialogTitle sx={{
                    color: theme.palette.text.primary,
                    borderBottom: `1px solid ${theme.palette.border.light}`,
                    fontWeight: 700,
                }}>
                    Create Ticket from Alert
                </DialogTitle>
                <DialogContent>
                    <NewTicketForm
                        onSuccess={() => { setCreateDialogOpen(false); setTicketCreatedSuccess(true); }}
                        initialData={{
                            alert_ids: [alert.id],
                            title: alert.title,
                            description: alert.description,
                            client_id: alert.client_id,
                            client_name: alert.client_name,
                            priority: mapSeverityToPriority(alert.severity),
                            riskScore: alert.risk_score,
                            raw_data: alert.raw_data,
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{
                    borderTop: `1px solid ${theme.palette.border.light}`,
                    p: 2,
                }}>
                    <Button
                        onClick={() => setCreateDialogOpen(false)}
                        sx={{
                            color: theme.palette.text.secondary,
                            '&:hover': {
                                backgroundColor: theme.palette.interactive.hover,
                            },
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={ticketCreatedSuccess}
                autoHideDuration={4000}
                onClose={() => setTicketCreatedSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <MuiAlert
                    onClose={() => setTicketCreatedSuccess(false)}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Ticket created successfully
                </MuiAlert>
            </Snackbar>
        </Box>
    );
};

export default AlertDetails;
